import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useLinkAccountant } from '~/api/auth';
import { Button, Card, ErrorMessage, Text, Title } from '~/components';
import { Field, Form, Input } from '~/components/Form';
import { EMAIL } from '~/constants/regex';
import { isNotBlank } from '~/utils/string';

import type { AccountantFormFields } from './RegistrationAccountantForm.types';

import {
  accountantFormStyle,
  formStyle,
  inputStyle,
  inputWrapperStyle,
  labelStyle,
  successTextStyle,
} from './RegistrationAccountantForm.css';

type Props = {
  userEmailAddress: string;
};

export const RegistrationAccountantForm = ({ userEmailAddress }: Props) => {
  const mutation = useLinkAccountant();

  const { control, handleSubmit } = useForm<AccountantFormFields>({
    defaultValues: { accountantEmailAddress: '' },
  });

  const { t } = useTranslation(['auth', 'common', 'validation']);

  const onSubmit = handleSubmit(({ accountantEmailAddress }) => {
    mutation.mutate({ userEmailAddress, accountantEmailAddress });
  });

  return (
    <Card.Root className={accountantFormStyle} variant="primary-light">
      {mutation.isSuccess ? (
        <>
          <Title as="h4">{t('auth:registrationPage.stepSuccess.linkAccountant.success.title')}</Title>

          <Text className={successTextStyle}>{t('auth:registrationPage.stepSuccess.linkAccountant.success.message')}</Text>
        </>
      ) : (
        <Form className={formStyle} onSubmit={onSubmit}>
          <Controller
            control={control}
            name="accountantEmailAddress"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
              <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                <Field.Label className={labelStyle}>
                  {t('auth:registrationPage.stepSuccess.linkAccountant.form.accountantEmailAddressField.label')}
                </Field.Label>

                <Field.Description>
                  {t('auth:registrationPage.stepSuccess.linkAccountant.form.accountantEmailAddressField.description')}
                </Field.Description>

                <div className={inputWrapperStyle}>
                  <div className={inputStyle}>
                    <Input onBlur={onBlur} onValueChange={onChange} ref={ref} type="email" value={value} />
                  </div>

                  <Button icon="Send" loading={mutation.isPending} type="submit">
                    {t('common:send')}
                  </Button>
                </div>

                <Field.Error match={!!error}>{error?.message}</Field.Error>
              </Field.Root>
            )}
            rules={{
              required: { value: true, message: t('validation:required') },
              validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
              pattern: { value: EMAIL, message: t('validation:email.invalid') },
            }}
          />

          {mutation.isError && <ErrorMessage>{t('auth:registrationPage.stepSuccess.linkAccountant.errors.generic')}</ErrorMessage>}
        </Form>
      )}
    </Card.Root>
  );
};
